/**
 * Security utility functions for input validation and sanitization
 */

/**
 * Sanitize user input to prevent XSS attacks
 * Removes dangerous HTML characters and tags
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  // Remove dangerous characters and HTML tags
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
    .trim()
    .substring(0, 500); // Limit length to prevent DoS
}

/**
 * Sanitize room names for multiplayer games
 */
export function sanitizeRoomName(name: string): string {
  if (!name) return '';
  
  // Allow only alphanumeric, spaces, and basic punctuation
  return name
    .replace(/[^a-zA-Z0-9\s\-_\.]/g, '')
    .trim()
    .substring(0, 50); // Limit room name length
}

/**
 * Validate and sanitize faction names
 */
export function validateFaction(faction: string): string {
  const validFactions = [
    'United States Of America',
    '3rd Reich',
    'United Kingdom',
    'USSR',
    'Imperial Japan',
    'Canada',
    'Fascist Italy'
  ];
  
  return validFactions.includes(faction) ? faction : validFactions[0];
}

/**
 * Create a text node safely (prevents XSS)
 */
export function createSafeTextElement(tag: string, text: string, className?: string): HTMLElement {
  const element = document.createElement(tag);
  element.textContent = text; // textContent is XSS-safe
  if (className) {
    element.className = className;
  }
  return element;
}

/**
 * Safely set text content of an element
 */
export function setSafeText(element: HTMLElement, text: string): void {
  element.textContent = text; // textContent is XSS-safe
}

/**
 * Create a safe HTML structure without using innerHTML
 */
export function createSafeHTML(parent: HTMLElement, structure: any): void {
  // Clear existing content safely
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }
  
  // Build new structure safely
  if (typeof structure === 'string') {
    parent.textContent = structure;
  } else if (structure instanceof HTMLElement) {
    parent.appendChild(structure);
  }
}

/**
 * Validate game state data to prevent tampering
 */
export function validateGameState(state: any): boolean {
  // Basic validation - should be expanded based on game rules
  if (!state || typeof state !== 'object') return false;
  
  // Check required fields exist
  const requiredFields = ['units', 'resources', 'turn', 'currentFaction'];
  for (const field of requiredFields) {
    if (!(field in state)) return false;
  }
  
  // Validate turn number
  if (typeof state.turn !== 'number' || state.turn < 0) return false;
  
  // Validate resources
  if (typeof state.resources !== 'object') return false;
  
  return true;
}

/**
 * Sanitize JSON data before parsing
 */
export function safeJSONParse(jsonString: string): any | null {
  try {
    // First, check if the string is not too large (prevent DoS)
    if (jsonString.length > 1000000) { // 1MB limit
      console.error('JSON string too large');
      return null;
    }
    
    const parsed = JSON.parse(jsonString);
    
    // Basic validation of parsed data
    if (parsed && typeof parsed === 'object') {
      // Remove any potentially dangerous properties
      delete parsed.__proto__;
      delete parsed.constructor;
      delete parsed.prototype;
    }
    
    return parsed;
  } catch (error) {
    console.error('Failed to parse JSON safely:', error);
    return null;
  }
}

/**
 * Escape HTML entities for safe display
 */
export function escapeHTML(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Validate move data for game actions
 */
export function validateMoveData(move: any): boolean {
  if (!move || typeof move !== 'object') return false;
  
  // Check move type
  const validTypes = ['move', 'attack', 'build', 'endTurn', 'surrender'];
  if (!validTypes.includes(move.type)) return false;
  
  // Validate coordinates if present
  if (move.position) {
    if (typeof move.position.x !== 'number' || typeof move.position.y !== 'number') return false;
    if (move.position.x < 0 || move.position.y < 0) return false;
    if (move.position.x > 100 || move.position.y > 100) return false; // Reasonable map limits
  }
  
  return true;
}